import { motion } from "framer-motion";
import { useWorkspaceCtx } from "@/contexts/WorkspaceCtx";
import { useCanvasStore } from "@/store/useCanvasStore";

// ── Funnel logic ─────────────────────────────────────────────────────────────

export type GuideStage =
  | "no-workspace"
  | "no-active-workspace"
  | "no-subject"
  | "no-forge"
  | "no-forge-selected"
  | "empty-forge"
  | null;

export interface GuideInput {
  loading: boolean;          // workspace list still loading
  menuOpen: boolean;         // mobile tree/menu overlay is open (covers the guide)
  forgeSelected: boolean;    // a forge is open in the viewport
  canvasLoading: boolean;    // the open forge's canvas is still fetching
  canvasEmpty: boolean;      // the open forge has no cards/shapes
  workspaceCount: number;
  activeWorkspaceId: string | null;
  activeSubjectCount: number;
  activeForgeCount: number;
}

/**
 * Pick the onboarding message that matches where the user is in the
 * workspace → subject → forge → canvas funnel. Returns null when no guidance
 * is needed (still loading, or the canvas already has content).
 */
export function computeGuideStage(i: GuideInput): GuideStage {
  if (i.loading) return null;
  // On mobile the tree/menu overlay opens on top of the canvas but sits below
  // the guide's z-index — so while it's open the guide must yield entirely and
  // let the menu cover the space, rather than float its copy over the menu.
  if (i.menuOpen) return null;
  // Never render over real content. On refresh the store rehydrates the last
  // forge from localStorage before any forge is "selected" again, so guard on
  // the canvas itself — not just on whether a forge is marked open.
  if (!i.canvasEmpty) return null;
  if (i.canvasLoading) return null;

  if (i.forgeSelected) return "empty-forge";

  if (i.workspaceCount === 0) return "no-workspace";
  if (!i.activeWorkspaceId) return "no-active-workspace";
  if (i.activeSubjectCount === 0) return "no-subject";
  if (i.activeForgeCount === 0) return "no-forge";
  return "no-forge-selected";
}

// ── Copy ─────────────────────────────────────────────────────────────────────

type Cue = "left" | "up";

interface StageContent {
  eyebrow: string;
  title: string;
  body: string;
  cue: Cue;
  cueLabel: string;
  // Optional per-stage headline font override. Defaults to the Fraunces
  // display face; set to opt a specific stage out of it.
  titleFontFamily?: string;
}

const INTER = "Inter, system-ui, sans-serif";

const CONTENT: Record<Exclude<GuideStage, null>, StageContent> = {
  "no-workspace": {
    eyebrow: "Welcome to Taitora",
    title: "Create your first workspace",
    body: "Workspaces hold your subjects and forges. Click the ＋ in the rail on the left to make one.",
    cue: "left",
    cueLabel: "New workspace",
  },
  "no-active-workspace": {
    eyebrow: "Pick up where you left off",
    title: "Choose a workspace",
    body: "Choose a workspace from the rail on the left — or create a brand-new one.",
    cue: "left",
    cueLabel: "Your workspaces",
    titleFontFamily: INTER,
  },
  "no-subject": {
    eyebrow: "Almost there",
    title: "Add your first subject",
    body: "Subjects group related forges. Hit the ＋ next to your workspace name to begin.",
    cue: "left",
    cueLabel: "New subject",
  },
  "no-forge": {
    eyebrow: "One more step",
    title: "Create a forge",
    body: "A forge is your canvas. Open a subject and add a forge to start building.",
    cue: "left",
    cueLabel: "Your subjects",
  },
  "no-forge-selected": {
    eyebrow: "Ready when you are",
    title: "Open a forge",
    body: "Pick a forge from the sidebar to load its canvas and jump back in.",
    cue: "left",
    cueLabel: "Your forges",
  },
  "empty-forge": {
    eyebrow: "Blank canvas",
    title: "Start forging",
    body: "Add your first card from the toolbar up top, or double-click anywhere to drop a node.",
    cue: "up",
    cueLabel: "Toolbar",
  },
};

// Mobile is view-only (no canvas editing) and every control lives on the left
// Rail, so a few stages read better with touch wording — and "empty-forge"
// can't say "add a card." Partial overrides merge over the desktop copy.
const MOBILE_CONTENT: Partial<Record<Exclude<GuideStage, null>, Partial<StageContent>>> = {
  "no-workspace": {
    body: "Workspaces hold your subjects and forges. Tap the ＋ in the rail on the left to make one.",
  },
  "no-subject": {
    body: "Subjects group related forges. Tap the ＋ next to your workspace name to begin.",
  },
  "no-forge": {
    body: "A forge is your canvas. Open a subject on the left and add a forge to get started.",
  },
  "no-forge-selected": {
    body: "Tap a workspace in the rail, then choose a forge to open its canvas.",
  },
  "empty-forge": {
    eyebrow: "Nothing here yet",
    title: "This forge is empty",
    body: "There's nothing on this canvas yet — pick another forge from the menu, or add content from the desktop app.",
    cue: "left",
    cueLabel: "Your forges",
  },
};

// ── Cute mini knowledge-graph illustration ───────────────────────────────────

const ACCENT = "#6366f1"; // indigo-500
const AMBER = "#D8943C";
const TEAL = "#38C6B5";

function GraphMark() {
  // Three nodes wired into a tiny graph, gently breathing. Echoes the canvas'
  // own node/edge language so the empty state feels like a preview of the thing
  // the user is about to build.
  return (
    <motion.svg
      width={132}
      height={104}
      viewBox="0 0 132 104"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      initial={{ y: 0 }}
      animate={{ y: [0, -6, 0] }}
      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      aria-hidden
    >
      {/* edges */}
      <motion.path
        d="M40 30 L66 62"
        stroke={ACCENT}
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeDasharray="4 5"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 0.5 }}
        transition={{ duration: 0.9, delay: 0.2 }}
      />
      <motion.path
        d="M92 30 L66 62"
        stroke={ACCENT}
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeDasharray="4 5"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 0.5 }}
        transition={{ duration: 0.9, delay: 0.35 }}
      />
      {/* nodes */}
      <motion.circle
        cx={40} cy={26} r={13} fill="#eef2ff" stroke={ACCENT} strokeWidth={2.5}
        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 220, damping: 14, delay: 0.05 }}
      />
      <motion.circle
        cx={92} cy={26} r={13} fill="#fdf3e6" stroke={AMBER} strokeWidth={2.5}
        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 220, damping: 14, delay: 0.18 }}
      />
      <motion.circle
        cx={66} cy={72} r={15} fill="#e7faf7" stroke={TEAL} strokeWidth={2.5}
        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 220, damping: 14, delay: 0.3 }}
      />
      {/* sparkle */}
      <motion.path
        d="M112 60 l2.6 6.4 6.4 2.6 -6.4 2.6 -2.6 6.4 -2.6 -6.4 -6.4 -2.6 6.4 -2.6 Z"
        fill={AMBER}
        initial={{ scale: 0, rotate: -30, opacity: 0 }}
        animate={{ scale: [0, 1.15, 1], rotate: 0, opacity: 1 }}
        transition={{ duration: 0.7, delay: 0.6 }}
        style={{ transformOrigin: "115px 69px" }}
      />
    </motion.svg>
  );
}

function CueArrow({ cue, label }: { cue: Cue; label: string }) {
  const isLeft = cue === "left";
  return (
    <motion.div
      className="flex items-center gap-1.5 text-canvas-accent/80"
      animate={isLeft ? { x: [0, -6, 0] } : { y: [0, -6, 0] }}
      transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
    >
      <span aria-hidden className="text-lg leading-none">{isLeft ? "←" : "↑"}</span>
      <span className="font-mono text-[11px] font-bold uppercase tracking-[0.1em]">{label}</span>
    </motion.div>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export default function CanvasGuide({ isMobile = false }: { isMobile?: boolean }) {
  const { projects, loading, activeWorkspaceId, isTreeOpen } = useWorkspaceCtx();
  // Source canvas state from the store, not ViewportCtx: the store is what
  // rehydrates from localStorage on refresh, so it's the truth about whether a
  // forge is open and whether its canvas has content.
  const forgeId = useCanvasStore((s) => s.forgeId);
  const canvasLoading = useCanvasStore((s) => s.isLoading);
  const canvasEmpty = useCanvasStore((s) => s.rfNodes.length === 0 && s.rfShapes.length === 0);

  const active = projects.find((p) => p.id === activeWorkspaceId) ?? null;
  const activeSubjectCount = active?.subjects.length ?? 0;
  const activeForgeCount = active?.subjects.reduce((n, s) => n + s.forges.length, 0) ?? 0;

  const stage = computeGuideStage({
    loading,
    // The tree is an inline column on desktop (no overlap) but a canvas overlay
    // on mobile — only then does it need to cover the guide.
    menuOpen: isMobile && isTreeOpen,
    forgeSelected: forgeId !== null,
    canvasLoading,
    canvasEmpty,
    workspaceCount: projects.length,
    activeWorkspaceId,
    activeSubjectCount,
    activeForgeCount,
  });

  if (!stage) return null;
  const c: StageContent = isMobile ? { ...CONTENT[stage], ...MOBILE_CONTENT[stage] } : CONTENT[stage];

  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
      <motion.div
        key={stage}
        data-testid="canvas-guide"
        className="flex flex-col items-center text-center px-8 max-w-[380px] select-none"
        initial={{ opacity: 0, y: 10, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
      >
        <GraphMark />

        <span className="mt-5 font-mono text-[11px] font-extrabold uppercase tracking-[0.14em] text-canvas-accent">
          {c.eyebrow}
        </span>
        <h2
          className={`mt-2 ${c.titleFontFamily ? "" : "font-display"} text-3xl font-semibold text-canvas-text leading-tight`}
          style={c.titleFontFamily ? { fontFamily: c.titleFontFamily } : undefined}
        >
          {c.title}
        </h2>
        <p className="mt-2.5 font-sans text-sm leading-relaxed text-canvas-muted">
          {c.body}
        </p>

        <div className="mt-5">
          <CueArrow cue={c.cue} label={c.cueLabel} />
        </div>
      </motion.div>
    </div>
  );
}
